import "./menu.css";
import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import AnimatedScroll from "../../components/AnimatedScroll/AnimatedScroll";
import { fetchMenusFromApi } from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";

function formatMenuDayDate(rawDate) {
  if (!rawDate || typeof rawDate !== "string") {
    return "";
  }

  const isoMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const date = new Date(year, month, day);

    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    }
  }

  const parsed = new Date(rawDate);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }

  return rawDate;
}

function Menu() {
  document.title = "Carpe Diem - Menu";

  const location = useLocation();
  const requestedMenuId = useMemo(() => {
    const value = new URLSearchParams(location.search).get("menuId");
    return String(value || "").trim();
  }, [location.search]);

  const [menusData, setMenusData] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState("");
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  // Charge le cache local rapidement, puis synchronise depuis l'API.
  useEffect(() => {
    let isMounted = true;

    const applyMenus = (menus) => {
      if (!isMounted) {
        return;
      }

      setMenusData(menus);
      setSelectedMenuId((currentId) => {
        if (
          requestedMenuId &&
          menus.some((menu) => menu.id === requestedMenuId)
        ) {
          return requestedMenuId;
        }

        if (currentId && menus.some((menu) => menu.id === currentId)) {
          return currentId;
        }
        return menus[0]?.id || "";
      });
    };

    const loadMenusFromLocalCache = () => {
      const savedMenus = localStorage.getItem("carpeDiemMenus");
      const menus = safeJsonParse(savedMenus, []);

      applyMenus(Array.isArray(menus) ? menus : []);
    };

    const syncMenusFromApi = async () => {
      try {
        const menus = await fetchMenusFromApi();
        if (!Array.isArray(menus) || menus.length === 0) {
          return;
        }

        localStorage.setItem("carpeDiemMenus", JSON.stringify(menus));
        applyMenus(menus);
      } catch (_error) {
        // Keep local cache if API is unavailable.
      }
    };

    loadMenusFromLocalCache();
    syncMenusFromApi();

    const handleStorageChange = (e) => {
      if (e.key === "carpeDiemMenus") {
        loadMenusFromLocalCache();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("carpe-content-changed", syncMenusFromApi);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncMenusFromApi();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("carpe-content-changed", syncMenusFromApi);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [requestedMenuId]);

  const currentMenu =
    menusData.find((m) => m.id === selectedMenuId) || menusData[0] || {};
  const hasMenus = menusData.length > 0;
  const isLinkMenu = currentMenu.menuType === "link";
  const formattedMenuDayDate = formatMenuDayDate(currentMenu.dateMenuJour);
  const menuHeader = (
    <div className={`menu-header${isLinkMenu ? " menu-header-link" : ""}`}>
      <h2 className="menu-titre">{currentMenu.titre}</h2>
      {currentMenu.soustitre && (
        <p className="menu-soustitre">{currentMenu.soustitre}</p>
      )}
      {formattedMenuDayDate && (
        <p className="menu-day-date">{formattedMenuDayDate}</p>
      )}
      {currentMenu.date && <p className="menu-date">{currentMenu.date}</p>}
      {currentMenu.amuse && <p className="menu-amuse">{currentMenu.amuse}</p>}
    </div>
  );

  useEffect(() => {
    setImageLoadFailed(false);
  }, [selectedMenuId, currentMenu.image]);

  // Fonction pour rendre une section
  const renderSection = (sectionKey) => {
    switch (sectionKey) {
      case "entrees":
        if (currentMenu.entrees && currentMenu.entrees.length > 0) {
          return (
            <div key="entrees" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titreEntrees || "Entrées"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.entrees.map((item, i) => {
                  if (typeof item === "string") {
                    return (
                      <li
                        key={i}
                        className={item === "OU" ? "ou-separator" : ""}
                      >
                        {item}
                      </li>
                    );
                  }
                  return (
                    <li key={i} className="plat-item">
                      <div className="plat-header">
                        <span className="plat-nom">{item.nom}</span>
                        {item.prix && (
                          <span className="plat-prix">{item.prix}</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="plat-description">{item.description}</p>
                      )}
                      {item.ou && <p className="ou-text">OU</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        }
        return null;

      case "plats":
        if (currentMenu.plats && currentMenu.plats.length > 0) {
          return (
            <div key="plats" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titrePlats || "Plats"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.plats.map((item, i) => {
                  if (typeof item === "string") {
                    return <li key={i}>{item}</li>;
                  }
                  return (
                    <li key={i} className="plat-item">
                      <div className="plat-header">
                        <span className="plat-nom">{item.nom}</span>
                        {item.prix && (
                          <span className="plat-prix">{item.prix}</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="plat-description">{item.description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        }
        return null;

      case "viandesPoissons":
        if (
          currentMenu.viandesPoissons &&
          currentMenu.viandesPoissons.length > 0
        ) {
          return (
            <div key="viandesPoissons" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titreViandesPoissons || "Viandes & Poissons"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.viandesPoissons.map((item, i) => (
                  <li key={i} className="plat-item">
                    <div className="plat-header">
                      <span className="plat-nom">{item.nom}</span>
                    </div>
                    {item.ou && <p className="ou-text">OU</p>}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return null;

      case "fromages":
        if (currentMenu.fromages && currentMenu.fromages.length > 0) {
          return (
            <div key="fromages" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titreFromages || "Fromages"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.fromages.map((item, i) => (
                  <li key={i} className="plat-item">
                    <span className="plat-nom">{item.nom}</span>
                    {item.ou && <p className="ou-subtext">{item.ou}</p>}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return null;

      case "desserts":
        if (currentMenu.desserts && currentMenu.desserts.length > 0) {
          return (
            <div key="desserts" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titreDesserts || "Desserts"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.desserts.map((item, i) => {
                  if (typeof item === "string") {
                    return <li key={i}>{item}</li>;
                  }
                  return (
                    <li key={i} className="plat-item">
                      <span className="plat-nom">{item.nom}</span>
                      {item.ou && <p className="ou-text">OU</p>}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        }
        return null;

      case "boissons":
        if (currentMenu.boissons && currentMenu.boissons.length > 0) {
          return (
            <div key="boissons" className="menu-section-detail">
              <h3 className="section-title">
                {currentMenu.titreBoissons || "Boissons"}
              </h3>
              <ul className="menu-list-plats">
                {currentMenu.boissons.map((item, i) => {
                  if (typeof item === "string") {
                    return <li key={i}>{item}</li>;
                  }
                  return (
                    <li key={i} className="plat-item">
                      <div className="plat-header">
                        <span className="plat-nom">{item.nom}</span>
                        {item.prix && (
                          <span className="plat-prix">{item.prix}</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="plat-description">{item.description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <>
      <section className="menu-section">
        <div className="Wrapper-sc">
          <div className="Menu-StyledContainer-sc">
            <div className="WmCustomContainer-sc WMContainer">
              {/* Bloc gauche : titre + sélecteur */}
              <div className="LeftBlock-sc">
                <AnimatedScroll>
                  <h1 className="Menu-Title-sc TitleStats">Nos Menus</h1>
                  <p className="Menu-Paragraph-sc">
                    Découvrez nos menus du jour et nos formules événements.
                  </p>

                  {/* Menu déroulant */}
                  <div className="menu-selector">
                    <label htmlFor="menu-select" className="menu-label">
                      Choisissez un menu :
                    </label>
                    <select
                      id="menu-select"
                      className="menu-select"
                      value={selectedMenuId}
                      disabled={!hasMenus}
                      onChange={(e) => setSelectedMenuId(e.target.value)}
                    >
                      {!hasMenus ? (
                        <option value="">Aucun menu disponible</option>
                      ) : (
                        menusData.map((menu) => (
                          <option key={menu.id} value={menu.id}>
                            {menu.titre}
                            {menu.soustitre ? ` - ${menu.soustitre}` : ""}
                            {formatMenuDayDate(menu.dateMenuJour)
                              ? ` - ${formatMenuDayDate(menu.dateMenuJour)}`
                              : menu.date
                                ? ` - ${menu.date}`
                                : ""}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <Link className="menu-allergens-link" to="/allergenes">
                    Voir le tableau des allergènes, fournisseurs et origines
                  </Link>
                </AnimatedScroll>
              </div>

              {/* Bloc droit : détails du menu sélectionné */}
              <div className="RightBlock-sc">
                <AnimatedScroll>
                  {!hasMenus ? (
                    <div className="menu-card menu-empty-state">
                      <h2 className="menu-titre">Aucun menu publié</h2>
                      <p className="menu-soustitre">
                        Le menu du jour sera bientôt disponible.
                      </p>
                    </div>
                  ) : (
                    <div className="menu-card">
                      {isLinkMenu && menuHeader}

                      {/* Image */}
                      {currentMenu.image && !imageLoadFailed && (
                        <div
                          className={`menu-image-wrapper${
                            isLinkMenu ? " menu-image-wrapper-link" : ""
                          }`}
                        >
                          <img
                            src={currentMenu.image}
                            alt={currentMenu.titre}
                            className={`menu-image${
                              isLinkMenu ? " menu-image-link" : ""
                            }`}
                            onError={() => setImageLoadFailed(true)}
                          />
                        </div>
                      )}

                      {(imageLoadFailed || !currentMenu.image) &&
                        isLinkMenu && (
                          <div className="menu-link-card">
                            <p className="menu-link-text">
                              Aucune image valide n'a été trouvée pour ce menu
                              lien.
                            </p>
                          </div>
                        )}

                      {!isLinkMenu && menuHeader}

                      {isLinkMenu ? null : (
                        <>
                          {/* Prix (si présents) */}
                          {currentMenu.prix && currentMenu.prix.length > 0 && (
                            <div className="menu-prix-list">
                              {currentMenu.prix.map((p, idx) => (
                                <div key={idx} className="menu-prix-item">
                                  <span className="prix-label">{p.label}</span>
                                  <span className="prix-montant">
                                    {p.montant}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Sections dans l'ordre personnalisé */}
                          {(
                            currentMenu.sectionsOrder || [
                              "entrees",
                              "plats",
                              "viandesPoissons",
                              "fromages",
                              "desserts",
                              "boissons",
                            ]
                          ).map((sectionKey) => renderSection(sectionKey))}

                          {/* Accompagnements (toujours à la fin, si présents) */}
                          {currentMenu.accompagnements &&
                            currentMenu.accompagnements.length > 0 && (
                              <div className="menu-section-detail">
                                <ul className="menu-list">
                                  {currentMenu.accompagnements.map(
                                    (item, i) => (
                                      <li key={i}>{item}</li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}

                          {/* Notes spéciales */}
                          {currentMenu.note && (
                            <div className="menu-note">
                              <p className="note-text">{currentMenu.note}</p>
                            </div>
                          )}

                          {/* Prix Note */}
                          {currentMenu.prixNote &&
                            currentMenu.prixNote.length > 0 && (
                              <div className="menu-prix-note">
                                {currentMenu.prixNote.map((p, idx) => (
                                  <p key={idx} className="prix-note-item">
                                    <strong>{p.label}</strong> {p.montant}
                                  </p>
                                ))}
                              </div>
                            )}

                          {/* Footer note */}
                          {currentMenu.noteFooter && (
                            <div className="menu-footer-note">
                              <p>{currentMenu.noteFooter}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </AnimatedScroll>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Menu;
